import { useEffect, useState } from 'react';
import { useGetCalculationsQuery, useGetCalculatorOfferingQuery } from '@app/api/calculatorApi';
import useDebounceValue from '@app/hooks/useDebounceValue';
import getMinMaxTermAndAmount from '@modules/calculator/utils/getMinMaxTermAndAmount';
const DEFAULT_AMOUNT = 0;
const DEFAULT_PERIOD = 0;
const useCalculator = ({ setIsValidCalculator, startAmount, startPeriod, onPeriodChange, onAmountChange, useOfferingQuery = useGetCalculatorOfferingQuery, useCalculationsQuery = useGetCalculationsQuery, }) => {
    const [loanAmount, setLoanAmount] = useState(startAmount || DEFAULT_AMOUNT);
    const [period, setPeriod] = useState(startPeriod || DEFAULT_PERIOD);
    const [minMaxPeriod, setMinMaxPeriod] = useState({ min: 0, max: 0 });
    const [minMaxAmount, setMinMaxAmount] = useState({ min: '0', max: '0' });
    const [errors, setErrors] = useState({});
    const debouncedLoanAmount = useDebounceValue(loanAmount, 500);
    const debouncedPeriod = useDebounceValue(period, 500);
    const { data: offeringData, isLoading } = useOfferingQuery();
    const { data: calculationsData, isLoading: isCalculationsLoading, isError, } = useCalculationsQuery({ amount: debouncedLoanAmount.toString(), period: debouncedPeriod.toString() }, { skip: debouncedLoanAmount <= 0 || debouncedPeriod <= 0 || Object.keys(errors).length > 0 });
    useEffect(() => {
        if (setIsValidCalculator) {
            setIsValidCalculator(!isError &&
                Object.keys(errors).length === 0 &&
                !isCalculationsLoading &&
                !(calculationsData === null || calculationsData === void 0 ? void 0 : calculationsData.isMonthlyPaymentLimitExceeded));
        }
    }, [errors, isCalculationsLoading, calculationsData]);
    useEffect(() => {
        if (offeringData && offeringData.length > 0) {
            const { minPeriod, minAmount, maxPeriod, maxAmount } = getMinMaxTermAndAmount(offeringData);
            setMinMaxAmount(() => ({ min: minAmount.toString(), max: maxAmount.toString() }));
            setMinMaxPeriod({ min: minPeriod, max: maxPeriod });
            if (loanAmount === DEFAULT_AMOUNT) {
                setLoanAmount(minAmount);
            }
            if (period === DEFAULT_PERIOD) {
                setPeriod(minPeriod);
            }
        }
    }, [offeringData]);
    useEffect(() => {
        if (debouncedLoanAmount > 0 && debouncedPeriod > 0 && Object.keys(errors).length === 0) {
            if (onAmountChange) {
                onAmountChange(debouncedLoanAmount);
            }
            if (onPeriodChange) {
                onPeriodChange(debouncedPeriod);
            }
        }
    }, [debouncedPeriod, debouncedLoanAmount]);
    const isPeriodValid = (selectedPeriod) => {
        if (!offeringData || selectedPeriod === 0) {
            return true;
        }
        const currentOptions = offeringData.filter(({ period: { min, max } }) => min <= selectedPeriod && max >= selectedPeriod);
        if (currentOptions.length > 0) {
            return currentOptions.some(({ amount: { min, max } }) => loanAmount <= Number(max) && loanAmount >= Number(min));
        }
        return false;
    };
    return {
        isLoading: isLoading || minMaxAmount.min === '0' || minMaxPeriod.min === 0,
        loanAmount,
        period,
        minMaxPeriod,
        minMaxAmount,
        setLoanAmount,
        setPeriod,
        isPeriodValid,
        errors,
        setErrors,
        isCalculationsLoading,
        calculationsData,
    };
};
export default useCalculator;
